import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import OrderForm from './OrderForm';
import ShoppingCartDisplay from './ShoppingCart';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ShoppingBag, ArrowLeft, ArrowRight, Plus, Minus, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { easterProducts } from '../data/easterProducts';
import { trackEvent } from '@/lib/analytics';

const PascoaProductPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart, cart, prepareForOrder } = useCart();

  const product = easterProducts.find(p => p.id === productId);
  const [currentImage, setCurrentImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);

  const selectedOption = product?.priceOptions?.[selectedOptionIndex] || null;
  const unitPrice = selectedOption ? selectedOption.price : product?.price;
  const unitPriceFormatted = selectedOption ? selectedOption.priceFormatted : product?.priceFormatted;
  const priceSuffix = product?.priceSuffix || '/ pack';

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="pt-24 flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-amber-800 text-lg mb-4">Produto não encontrado.</p>
            <Button onClick={() => navigate('/pascoa')} variant="outline">
              <ChevronLeft className="w-4 h-4 mr-1" /> Voltar
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    const totalPrice = (unitPrice * quantity).toFixed(2).replace('.', ',');
    addToCart({
      productType: selectedOption ? `${product.name} - ${selectedOption.label}` : product.name,
      quantity,
      flavors: [],
      price: `€${totalPrice}`,
    });
    trackEvent('add_to_cart', {
      item_name: selectedOption ? `${product.name} - ${selectedOption.label}` : product.name,
      quantity,
      currency: 'EUR',
      value: Number((unitPrice * quantity).toFixed(2)),
    });
    toast.success(`${quantity}x ${selectedOption ? `${product.name} (${selectedOption.label})` : product.name} adicionado ao carrinho`);
  };

  const handleFinalizeOrder = () => {
    if (!cart.length) {
      toast.error('Adicione produtos ao carrinho antes de finalizar o pedido');
      return;
    }

    const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
    trackEvent('begin_checkout', {
      items: totalItems,
      currency: 'EUR',
    });

    prepareForOrder();
    setTimeout(() => {
      const orderSection = document.getElementById('order');
      if (orderSection) {
        orderSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-amber-50/50 flex flex-col">
      <Header />
      <main className="pt-24 flex-1">
        <div className="container mx-auto px-4 max-w-5xl py-10">
          {/* Back button */}
          <button
            type="button"
            onClick={() => navigate('/pascoa')}
            className="flex items-center gap-1 text-amber-700 hover:text-amber-900 text-sm font-medium mb-8 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Voltar aos produtos
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
            {/* Image Gallery */}
            <div className="flex flex-col gap-4">
              <div className="relative rounded-2xl overflow-hidden bg-amber-50 aspect-square">
                <img
                  src={product.images[currentImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                {product.images.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setCurrentImage(p => (p === 0 ? product.images.length - 1 : p - 1))}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-amber-900 shadow-lg flex items-center justify-center transition-colors"
                      aria-label="Imagem anterior"
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentImage(p => (p + 1) % product.images.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-amber-900 shadow-lg flex items-center justify-center transition-colors"
                      aria-label="Próxima imagem"
                    >
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              {/* Thumbnails */}
              {product.images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto max-w-full pb-1">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setCurrentImage(idx)}
                      className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all flex-shrink-0 ${
                        idx === currentImage ? 'border-orange-500 shadow-md' : 'border-amber-200 opacity-60 hover:opacity-100'
                      }`}
                      aria-label={`Foto ${idx + 1}`}
                    >
                      <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="flex flex-col gap-5">
              <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-200 w-fit">
                Edição de Páscoa 2026
              </Badge>

              <h1 className="text-3xl md:text-4xl font-bold text-amber-950">{product.name}</h1>

              <p className="text-amber-700 leading-relaxed text-base">{product.description}</p>

              <ul className="space-y-2">
                {product.items.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-amber-800">
                    <span className="text-orange-500 font-bold mt-0.5">•</span>
                    <span className="text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>

              {product.priceOptions?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-amber-900">Escolha a caixa:</p>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                    {product.priceOptions.map((option, idx) => (
                      <button
                        key={option.label}
                        type="button"
                        onClick={() => setSelectedOptionIndex(idx)}
                        className={`text-left rounded-xl border-2 p-3 transition-colors ${
                          idx === selectedOptionIndex
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-amber-200 bg-white hover:border-amber-300'
                        }`}
                      >
                        <p className="text-sm font-semibold text-amber-950">{option.label}</p>
                        <p className="text-xs text-amber-700">{option.flavors}</p>
                        <p className="text-sm font-bold text-orange-700 mt-1">{option.priceFormatted}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-4xl font-bold text-orange-800">
                {unitPriceFormatted}{' '}
                <span className="text-base font-normal text-amber-600">{priceSuffix}</span>
              </div>

              {/* Quantity */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-amber-900">Quantidade:</span>
                <button
                  type="button"
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="w-9 h-9 rounded-full bg-amber-100 hover:bg-amber-200 flex items-center justify-center text-amber-900 transition-colors"
                  aria-label="Reduzir quantidade"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-bold text-amber-950 text-lg">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity(q => q + 1)}
                  className="w-9 h-9 rounded-full bg-amber-100 hover:bg-amber-200 flex items-center justify-center text-amber-900 transition-colors"
                  aria-label="Aumentar quantidade"
                >
                  <Plus className="w-4 h-4" />
                </button>
                {quantity > 1 && (
                  <span className="text-sm text-orange-700 font-semibold">
                    Total: €{(unitPrice * quantity).toFixed(2).replace('.', ',')}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  onClick={handleAddToCart}
                  className="bg-orange-700 hover:bg-orange-800 text-white rounded-full py-6 text-base font-semibold"
                >
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  Adicionar ao Carrinho
                </Button>
                <Button
                  onClick={handleFinalizeOrder}
                  variant="outline"
                  className="border-2 border-amber-300 text-amber-900 hover:bg-amber-100 rounded-full py-6 text-base font-semibold"
                >
                  Finalizar Pedido
                </Button>
              </div>
            </div>
          </div>
        </div>

        <section className="py-12 bg-amber-50/60">
          <div className="container mx-auto px-4 max-w-lg" id="pascoa-cart">
            <ShoppingCartDisplay />
          </div>
        </section>

        <OrderForm />
      </main>
      <Footer />
    </div>
  );
};

export default PascoaProductPage;
